import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import Link from 'fe-design-base/molecules/Link';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import PropTypes from 'prop-types';

import { getCurrentLocationId } from 'selectors/session';

import {
  createChannel,
  toggleMessengerShown,
} from 'features/messengerv2/actions';

import { cxHelpers } from 'util/className';
import { formatPhoneNumber } from 'util/formatter';
import { toI18n } from 'util/i18n';
import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

const { cx, cxEl } = cxHelpers('ActionButtons');

const ActionButtons = ({
  userId,
  userFirstName,
  userPhone,
  onToggleMessengerShown,
  onCreateNewChannel,
  isMobile,
  closeModal,
  currentLocationId,
}) => {
  const isEmbedded = useIsEmbedded();

  const handleOpenMessenger = useCallback(() => {
    closeModal();
    trackUxEvent({
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.MESSAGE_CLICKED_V3,
    });

    onCreateNewChannel({
      prefilledMessage: ' ',
      location_id: currentLocationId,
      channel: { participants_attributes: [{ user_id: userId }] },
    });
    onToggleMessengerShown(true);
  }, [
    closeModal,
    onCreateNewChannel,
    currentLocationId,
    userId,
    onToggleMessengerShown,
  ]);

  const handleCallButtonClicked = useCallback(() => {
    trackUxEvent({
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.CALL_CLICKED_V3,
    });
  }, []);

  return (
    <Box row vcenter className={cx()} h={24} style={{ marginTop: '8px' }}>
      {userPhone && (
        <Box row>
          <Box w={150} mr={10} vcenter className={cxEl('phone')}>
            <Box mr={8}>
              <Icon
                iconName="Phone"
                size="small"
                color={isMobile ? 'purple500' : 'mono700'}
              />
            </Box>
            {isMobile ? (
              <Link href={`tel:${userPhone}`} onClick={handleCallButtonClicked}>
                {formatPhoneNumber(userPhone)}
              </Link>
            ) : (
              <Text variant="body" color="mono700">
                {formatPhoneNumber(userPhone)}
              </Text>
            )}
          </Box>

          {!isEmbedded && (
            <Text variant="body" color="mono700">
              |
            </Text>
          )}
        </Box>
      )}
      {!isEmbedded && (
        <Box
          className={cxEl('message')}
          style={userPhone ? { marginLeft: '8px' } : null}
          noWrap
        >
          <Button
            variant="tertiary"
            startIcon="Chat"
            onClick={handleOpenMessenger}
          >
            {toI18n('timecard_modal_v3.action_buttons.message', {
              props: {
                name: userFirstName,
              },
            })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

ActionButtons.propTypes = {
  currentLocationId: PropTypes.number.isRequired,
  userId: PropTypes.number,
  onCreateNewChannel: PropTypes.func.isRequired,
  onToggleMessengerShown: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default connect(
  state => ({
    currentLocationId: getCurrentLocationId(state),
  }),
  {
    onCreateNewChannel: createChannel,
    onToggleMessengerShown: toggleMessengerShown,
  }
)(ActionButtons);
